
import React from 'react';
import { Wallet, TreePine, Shield, Clock, Users, Globe } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

export const BenefitsSection = () => {
  const { t } = useLanguage();
  const benefits = [
    {
      icon: Wallet,
      title: t('saveMoneyTitle', 'benefits'),
      description: t('saveMoneyDesc', 'benefits'),
      stats: t('saveMoneyStats', 'benefits'),
      gradient: 'from-emerald-500/20 to-green-600/20'
    },
    {
      icon: TreePine,
      title: t('helpPlanetTitle', 'benefits'),
      description: t('helpPlanetDesc', 'benefits'),
      stats: t('helpPlanetStats', 'benefits'),
      gradient: 'from-green-500/20 to-emerald-600/20'
    },
    {
      icon: Shield,
      title: t('travelSafeTitle', 'benefits'),
      description: t('travelSafeDesc', 'benefits'),
      stats: t('travelSafeStats', 'benefits'),
      gradient: 'from-blue-500/20 to-indigo-600/20'
    },
    {
      icon: Clock,
      title: t('anyTimeTitle', 'benefits'),
      description: t('anyTimeDesc', 'benefits'),
      stats: t('anyTimeStats', 'benefits'),
      gradient: 'from-purple-500/20 to-violet-600/20'
    },
    {
      icon: Users,
      title: t('meetPeopleTitle', 'benefits'),
      description: t('meetPeopleDesc', 'benefits'),
      stats: t('meetPeopleStats', 'benefits'),
      gradient: 'from-pink-500/20 to-rose-600/20'
    },
    {
      icon: Globe,
      title: t('goAnywhereTitle', 'benefits'),
      description: t('goAnywhereDesc', 'benefits'),
      stats: t('goAnywhereStats', 'benefits'),
      gradient: 'from-orange-500/20 to-red-600/20'
    }
  ];

  return (
    <section id="benefits" className="relative pt-8 pb-12 bg-gradient-to-br from-gray-50 via-white to-blue-50 overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-gradient-to-br from-[#22C55E]/10 to-transparent rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-gradient-to-br from-blue-400/10 to-transparent rounded-full blur-2xl animate-float-reverse"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block bg-white/40 backdrop-blur-md border border-white/30 rounded-full px-6 py-2 mb-6 shadow-lg">
            <span className="text-sm font-medium text-[#22C55E]">{t('badge', 'benefits')}</span>
          </div>
          <h2 className="text-5xl md:text-7xl font-light text-gray-900 mb-6 animate-slide-up">
            {t('sectionTitle', 'benefits')}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light animate-fade-in-delayed">
            {t('sectionSubtitle', 'benefits')}
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className={`group relative bg-white/30 backdrop-blur-xl p-8 rounded-3xl border border-white/40 shadow-xl hover:shadow-2xl transition-all duration-700 hover:scale-105 animate-fade-in-up`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Gradient overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Content */}
              <div className="relative z-10">
                <div className="w-16 h-16 mb-6 rounded-2xl bg-white/50 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#22C55E] transition-all duration-500 shadow-lg">
                  <benefit.icon className="w-8 h-8 text-[#22C55E] group-hover:text-white transition-colors duration-500" />
                </div>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors duration-300">
                  {benefit.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed font-light group-hover:text-gray-700 transition-colors duration-300">
                  {benefit.description}
                </p>
                
                <div className="bg-white/60 backdrop-blur-sm text-[#22C55E] px-4 py-2 rounded-full inline-block text-sm font-medium shadow-sm group-hover:bg-white/80 transition-all duration-300">
                  {benefit.stats}
                </div>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-shimmer"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
